import dayjs from 'dayjs';
import Inherit from '../../../core/inherit';
import ClientDelegate from './client.delegate';
import ShelfController from '../../../core/shelf.controller';
import { calculateAuditDuration } from '../../../utils/timeUtils';
import { User } from '../../../data';

class ClientController extends Inherit(ShelfController, ClientDelegate) {
    shouldComponentUpdate(nextProps, nextState) {
        return (
            this.state.client !== nextState.client ||
            this.state.gerente !== nextState.gerente ||
            this.state.loading !== nextState.loading ||
            this.state.stores !== nextState.stores ||
            this.state.filtered !== nextState.filtered ||
            this.state.recentAudits !== nextState.recentAudits ||
            this.state.elementsLoading !== nextState.elementsLoading ||
            this.state.selectedStores !== nextState.selectedStores ||
            this.state.contracts !== nextState.contracts ||
            this.state.campaigns !== nextState.campaigns
        );
    }

    async initialize() {
        let client = null;
        let asesores = null;
        let gerente = null;
        let stores = null;
        let recentAudits = null;
        let storeTypes = null;

        const today = dayjs();

        if (!this.isAuthenticated) {
            this.navigateLogin();
            return;
        }

        const currentUser = User.getInstance();
        const isClientUser = currentUser?.userType === 'client';
        const requestedId = this.props.searchParams.get('id');

        // Client users can only view their own client profile
        if (isClientUser) {
            const ownClientId = currentUser?.client?.toString();
            if (!requestedId || requestedId !== ownClientId) {
                if (ownClientId) {
                    this.props.navigate(`/dashboard/clients/client?id=${ownClientId}`);
                } else {
                    this.props.navigate('/dashboard');
                }
                return;
            }
        }

        let contracts = [];
        let campaigns = [];

        if (this.isAuthenticated) {
            client = (await this.data.fetchClientById(requestedId)) || {};
            asesores = await this.getAsesores(client);
            gerente = client?.gerente ? await this.data.fetchUserById(client.gerente) : null;
            stores = await this.fetchStores();
            recentAudits = await this.fetchStoreChecks(today.startOf('month'), today.endOf('month'), true);
            storeTypes = await this.data.fetchStoreTypes();
            const [rawContracts, rawCampaigns] = await Promise.all([
                this.data.fetchContractsByClient(requestedId).catch(() => []),
                this.data.fetchCampaignsByClient(requestedId).catch(() => []),
            ]);
            contracts = Array.isArray(rawContracts) ? rawContracts : [];
            campaigns = Array.isArray(rawCampaigns) ? rawCampaigns : [];
        }

        this.setState({
            client,
            asesores,
            gerente,
            stores,
            recentAudits,
            storeTypes,
            contracts,
            campaigns,
            filtered: stores,
            loading: false,
            elementsLoading: false
        });
    }

    /** Client users can't open the clients list, so the "Volver" link is hidden for them. */
    get isClientUser() {
        return User.getInstance()?.userType === 'client';
    }

    async getAsesores(client) {
        if (!client?.asesor?.length) return [];

        return (await Promise.all(client.asesor.map((id) => this.data.fetchUserById(id)))) || [];
    }

    async fetchStores() {
        try {
            const stores = await this.data.fetchStoresByClient(this.props.searchParams.get('id'));
            return stores || [];
        } catch (error) {
            console.error('Error fetching stores:', error);
            return [];
        }
    }

    async fetchStoreChecks(startDate, endDate, isInitial = false) {
        try {
            const storechecks = await this.data.fetchStoreChecksByClient(this.props.searchParams.get('id'), startDate, endDate);
            const users = new Map((await this.data.fetchUsers())?.map((user) => [user._id, user]));

            if (!storechecks?.length > 0 || !storechecks) return [];

            const recentAudits = await Promise.all(
                storechecks?.map(async (check) => {
                    const user = users.get(check?.userId);

                    // Use shared utility for time calculation (business logic in utils)
                    const totalTime = calculateAuditDuration(check);

                    return {
                        ...check,
                        userName: user?.name,
                        totalTime,
                        storeName: check?.storeId?.name,
                        storeType: check?.storeId?.storeType
                    };
                }) ?? []
            );

            // Sort by createdAt from newest to oldest
            const sortedAudits = recentAudits.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

            if (isInitial) return sortedAudits;
            else this.setState({ recentAudits: sortedAudits, elementsLoading: false });
        } catch (err) {
            console.error('Error fetching store checks:', err);
            return isInitial ? [] : undefined;
        }
    }
}

export default ClientController;
